import { createClient } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
if (!supabaseUrl || !supabaseKey) {
  throw new Error('Supabase env vars not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.');
}

const supabase = createClient(supabaseUrl, supabaseKey);

export interface WeakArea {
  topic: string;
  score: number;
}

export interface UserProgressData {
  userId: string;
  lastExamScore: number | null;
  lastExamDate: string | null;
  weakAreas: WeakArea[];
  completedWeakAreas: string[];
  updatedAt: string;
}

export async function getUserProgress(userId: string): Promise<UserProgressData | null> {
  try {
    const { data, error } = await supabase
      .from('user_progress')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    return {
      userId: data.user_id,
      lastExamScore: data.last_exam_score,
      lastExamDate: data.last_exam_date,
      weakAreas: data.weak_areas || [],
      completedWeakAreas: data.completed_weak_areas || [],
      updatedAt: data.updated_at
    };
  } catch (error) {
    logger.error('Error fetching user progress:', error);
    return null;
  }
}

export async function saveExamResults(
  userId: string,
  score: number,
  weakAreas: WeakArea[]
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_progress')
      .upsert({
        user_id: userId,
        last_exam_score: score,
        last_exam_date: new Date().toISOString(),
        weak_areas: weakAreas,
        completed_weak_areas: [],
        updated_at: new Date().toISOString()
      });

    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('Error saving exam results:', error);
    return false;
  }
}

export async function markWeakAreaCompleted(
  userId: string,
  weakAreaTopic: string
): Promise<boolean> {
  try {
    const progress = await getUserProgress(userId);
    if (!progress) return false;

    const updatedCompletedAreas = [...progress.completedWeakAreas, weakAreaTopic];

    const { error } = await supabase
      .from('user_progress')
      .update({
        completed_weak_areas: updatedCompletedAreas,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('Error marking weak area as completed:', error);
    return false;
  }
}

export async function resetWeakAreasForNewExam(userId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('user_progress')
      .update({
        completed_weak_areas: [],
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);

    if (error) throw error;
    return true;
  } catch (error) {
    logger.error('Error resetting weak areas:', error);
    return false;
  }
}
